import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueueEntry } from '@his/shared';
import { ApiError, get, post, put, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { useWorkingBranchId } from '@/lib/branch';
import { humanize, todayIso } from '@/lib/utils';
import { DataTable, FilterBar, FilterField, PageHeader } from '@/components/ui/layout';
import { Alert, Badge, Button, DatePicker, Field, Input, Select, StatusBadge } from '@/components/ui/primitives';
import { Dialog } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { VitalsDialog } from '@/components/VitalsDialog';
import { queueNewbornFacts } from './emr/completion';
import { ReferralsCard } from './VisitOrdersPanel';
import { fallbackInterval, useRealtimeInvalidate } from '@/lib/realtime';
import { initialQueueDoctorId } from './queue-scope';
import { isFeeGate, unpaidWithDoctor } from './fee-gate';
import { CheckUpBadge } from '../billing/PackageLineBadge';

const FEE_LABEL: Record<string, { label: string; tone: 'success' | 'warning' | 'muted' | 'destructive' | 'secondary' }> = {
  free: { label: 'free follow-up', tone: 'success' },
  paid: { label: 'fee paid', tone: 'success' },
  due: { label: 'fee due', tone: 'warning' },
  insured_paid: { label: 'insured', tone: 'success' },
  insured_due: { label: 'insured · co-pay due', tone: 'warning' },
  not_billed: { label: 'not billed yet', tone: 'muted' },
  no_price: { label: 'no fee set', tone: 'destructive' },
};

export function isBackEntry(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409 && error.details.some((detail) => detail.path === 'backdateReason' && detail.message === 'required');
}

const STAGE_LABEL: Record<string, string> = {
  waiting: 'waiting',
  with_nurse: 'with nurse',
  ready: 'ready for doctor',
  called: 'called',
};

interface DoctorOption {
  id: string;
  name: string;
}

/**
 * The doctor's working queue (SRS 6.3): who is waiting, how long they have
 * waited, and whether triage and the fee are done — the three things that
 * decide whether the next patient can actually be seen.
 */
export function QueuePage() {
  const { user, can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [doctorId, setDoctorId] = useState<string>(initialQueueDoctorId(user));
  const [date, setDate] = useState('');
  const [takingVitals, setTakingVitals] = useState<QueueEntry | null>(null);
  const [overrideFor, setOverrideFor] = useState<QueueEntry | null>(null);
  const [overrideKind, setOverrideKind] = useState<'fee' | 'backdate'>('fee');
  const [overrideReason, setOverrideReason] = useState('');

  const doctors = useQuery({
    queryKey: ['bookable-doctors'],
    queryFn: async () => {
      const result = await get<{ data: DoctorOption[] }>('/org/clinicians?kind=bookable&limit=200');
      return result.data;
    },
    enabled: can('appointment.read', 'appointment.manage'),
  });

  const branchId = useWorkingBranchId();
  const live = useRealtimeInvalidate(doctorId ? [`queue:${doctorId}`] : branchId ? [`queue-branch:${branchId}`] : [], null, [['queue']]);

  const queue = useQuery({
    queryKey: ['queue', doctorId, date],
    queryFn: () =>
      get<QueueEntry[]>(
        `/queue${qs({ doctorId: doctorId || undefined, allDoctors: doctorId ? undefined : true, date: date || undefined })}`,
      ),
    // A waiting room changes constantly; the board should not need a refresh.
    refetchInterval: fallbackInterval(live, 60_000),
  });
  const unpaidInConsultation = unpaidWithDoctor(queue.data ?? []);

  const startConsultation = useMutation({
    mutationFn: async ({ entry, feeOverrideReason, backdateReason }: { entry: QueueEntry; feeOverrideReason?: string; backdateReason?: string }) => {
      const encounter = await post<{ id: string }>('/encounters', {
        patientId: entry.patientId,
        appointmentId: entry.appointmentId,
        context: 'opd',
        ...(feeOverrideReason ? { feeOverrideReason } : {}),
        ...(backdateReason ? { backdateReason } : {}),
      });
      return encounter.id;
    },
    onSuccess: (encounterId) => {
      setOverrideFor(null);
      setOverrideReason('');
      navigate(`/consultations/${encounterId}`);
    },
    onError: (error, variables) => {
      if (isFeeGate(error) && can('opd.fee.override') && !variables.feeOverrideReason) {
        setOverrideKind('fee');
        setOverrideFor(variables.entry);
        return;
      }
      if (isBackEntry(error) && can('encounter.backdate') && !variables.backdateReason) {
        setOverrideKind('backdate');
        setOverrideFor(variables.entry);
        return;
      }
      toast.error(tr('opd', 'could_not_open_the_consultation'), describeError(error));
    },
  });

  const call = useMutation({
    mutationFn: (entry: QueueEntry) => post(`/appointments/${entry.appointmentId}/call`, { room: entry.room ?? undefined }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['queue'] }),
    onError: (error) => toast.error(tr('opd', 'could_not_call_the_patient'), describeError(error)),
  });

  const callNext = useMutation({
    mutationFn: () => post<{ tokenNo: string | null }>('/queue/call-next', { doctorId }),
    onSuccess: (called) => {
      toast.success(called.tokenNo ? 'Token ' + called.tokenNo + ' called' : tr('opd', 'next_patient_called'));
      void queryClient.invalidateQueries({ queryKey: ['queue'] });
    },
    onError: (error) => toast.error(tr('opd', 'nobody_to_call'), describeError(error)),
  });

  const billNow = useMutation({
    mutationFn: (entry: QueueEntry) => post<{ billed: boolean }>(`/appointments/${entry.appointmentId}/bill`, {}),
    onSuccess: (result) => {
      if (result.billed) toast.success(tr('opd', 'visit_billed'));
      else toast.error(tr('opd', 'could_not_bill_the_visit'), tr('opd', 'billing_still_blocked'));
      void queryClient.invalidateQueries({ queryKey: ['queue'] });
    },
    onError: (error) => toast.error(tr('opd', 'could_not_bill_the_visit'), describeError(error)),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      put(`/appointments/${id}/status`, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['queue'] });
    },
    onError: (error) => toast.error(tr('opd', 'could_not_update_the_appointment'), describeError(error)),
  });

  return (
    <>
      <PageHeader
        title={tr('opd', 'queue')}
        description={tr('opd', 'patients_who_have_arrived_in_the_order_they')}
        actions={
          doctorId &&
          can('encounter.write', 'vitals.write', 'token.issue') && (
            <Button variant="outline" onClick={() => callNext.mutate()} loading={callNext.isPending}>
              {tr('opd', 'call_next')}
            </Button>
          )
        }
      />

      {can('encounter.write') && <ReferralsCard scope="mine" />}

      {unpaidInConsultation > 0 && (
        <Alert variant="warning" title={tr('opd', 'consultation_fee_unpaid')} className="mb-4">
          {tr('opd', 'x_with_the_doctor_have_not_paid_the_fee', { count: unpaidInConsultation })}
        </Alert>
      )}

      <FilterBar>
        {doctors.data && doctors.data.length > 0 && (
          <FilterField label={tr('opd', 'doctor')}>
            <Select value={doctorId} onChange={(e) => setDoctorId(e.target.value)} className="w-56">
              <option value="">{tr('opd', 'all_doctors')}</option>
              {doctors.data.map((doctor) => (
                <option key={doctor.id} value={doctor.id}>
                  {doctor.name}
                </option>
              ))}
            </Select>
          </FilterField>
        )}
        <FilterField label={tr('opd', 'date')}>
          <DatePicker value={date || todayIso()} onChange={setDate} className="w-40" />
        </FilterField>
      </FilterBar>

      <DataTable
        rows={queue.data}
        loading={queue.isLoading} refreshing={queue.isFetching}
        error={queue.error}
        rowKey={(row) => row.appointmentId}
        empty={tr('opd', 'nobody_is_waiting_patients_appear_here_once')}
        columns={[
          {
            header: tr('opd', 'token'),
            width: '80px',
            sort: (row) => row.tokenNo,
            cell: (row) => (
              <span className="tabular text-base font-semibold">{row.tokenNo ?? '—'}</span>
            ),
          },
          {
            header: tr('opd', 'patient'),
            sort: (row) => row.patientName,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.patientName}</p>
                <p className="text-xs text-muted-foreground">
                  {row.mrn} · {row.ageLabel} · {humanize(row.sex)}
                </p>
              </div>
            ),
          },
          { header: tr('opd', 'visit'), sort: (row) => row.type, cell: (row) => <Badge variant="secondary">{humanize(row.type)}</Badge> },
          {
            header: tr('opd', 'waiting'),
            align: 'right',
            sort: (row) => row.waitingMinutes,
            cell: (row) =>
              row.waitingMinutes === null ? (
                <span className="text-muted-foreground">—</span>
              ) : (
                <span className={row.waitingMinutes > 45 ? 'font-semibold text-destructive' : ''}>
                  {row.waitingMinutes} {tr('opd', 'min')}
                </span>
              ),
          },
          {
            header: tr('opd', 'ready'),
            cell: (row) => (
              <div className="flex gap-1">
                <Badge variant={row.hasVitals ? 'success' : 'muted'}>
                  {row.hasVitals ? tr('opd', 'vitals') : tr('opd', 'no_vitals')}
                </Badge>
                {row.feeState ? (
                  <Badge variant={FEE_LABEL[row.feeState]?.tone ?? 'muted'} title={row.feeState === 'no_price' ? tr('opd', 'no_fee_set_for_this_doctor_tell_the_billing') : undefined}>
                    {FEE_LABEL[row.feeState]?.label ?? row.feeState}
                  </Badge>
                ) : (
                  <Badge variant={row.feePaid ? 'success' : 'warning'}>{row.feePaid ? tr('opd', 'fee_paid') : tr('opd', 'fee_due')}</Badge>
                )}
                {row.feeState === 'no_price' && can('charge.manage') && (
                  <Link to="/billing/charges" className="text-xs text-primary underline">
                    {tr('opd', 'set_fee')}
                  </Link>
                )}
                {row.billingIssue && can('invoice.create') && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => billNow.mutate(row)}
                    loading={billNow.isPending && billNow.variables?.appointmentId === row.appointmentId}
                  >
                    {tr('opd', 'bill_now')}
                  </Button>
                )}
              </div>
            ),
          },
          {
            header: tr('opd', 'status'),
            sort: (row) => row.status,
            cell: (row) => (
              <div className="flex flex-wrap gap-1">
                <StatusBadge status={row.status} />
                {row.stage && row.status === 'arrived' && <Badge variant="secondary">{STAGE_LABEL[row.stage] ?? row.stage}</Badge>}
                {row.priority === 'urgent' && <Badge variant="destructive">{tr('opd', 'see_first')}</Badge>}
                {row.abnormalVitals && <Badge variant="warning">{tr('opd', 'abnormal_vitals')}</Badge>}
                {row.payerName && <Badge variant="outline">{row.payerName}</Badge>}
                {row.packageName && <CheckUpBadge name={row.packageName} />}
              </div>
            ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <div className="flex justify-end gap-1">
                {(row.status === 'booked' || row.status === 'confirmed') && can('appointment.manage', 'token.issue') ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setStatus.mutate({ id: row.appointmentId, status: 'arrived' })}
                    loading={setStatus.isPending}
                  >
                    {tr('opd', 'mark_arrived')}
                  </Button>
                ) : null}
                {!row.hasVitals && can('vitals.write') && ['arrived', 'in_consultation'].includes(row.status) && (
                  <Button size="sm" variant="outline" onClick={() => setTakingVitals(row)}>
                    {tr('opd', 'vitals_2')}
                  </Button>
                )}
                {row.status === 'arrived' && can('encounter.write', 'vitals.write', 'token.issue') && (
                  <Button size="sm" variant="outline" onClick={() => call.mutate(row)} loading={call.isPending && call.variables?.appointmentId === row.appointmentId}>
                    {row.calledAt ? tr('opd', 'call_again') : tr('opd', 'call')}
                  </Button>
                )}
                {can('encounter.write') && ['arrived', 'in_consultation'].includes(row.status) && (
                  <Button size="sm" onClick={() => startConsultation.mutate({ entry: row })} loading={startConsultation.isPending}>
                    {row.status === 'in_consultation' ? tr('opd', 'resume') : tr('opd', 'start')}
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />

      {overrideFor && (
        <Dialog
          open
          onClose={() => setOverrideFor(null)}
          title={overrideKind === 'fee' ? tr('opd', 'fee_not_paid_yet') : tr('opd', 'late_entry_of_an_earlier_visit')}
          footer={
            <>
              <Button variant="ghost" onClick={() => setOverrideFor(null)}>
                {tr('opd', 'cancel')}
              </Button>
              <Button
                disabled={overrideReason.trim().length < 5}
                loading={startConsultation.isPending}
                onClick={() =>
                  startConsultation.mutate(
                    overrideKind === 'fee'
                      ? { entry: overrideFor, feeOverrideReason: overrideReason.trim() }
                      : { entry: overrideFor, backdateReason: overrideReason.trim() },
                  )
                }
              >
                {tr('opd', 'start_anyway')}
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <Alert variant="warning">
              {overrideKind === 'fee'
                ? tr('opd', 'x_has_not_paid_the_consultation_fee_collect_it', { patientName: overrideFor.patientName })
                : tr('opd', 'this_visit_was_on_an_earlier_clinic_day_it_is')}
            </Alert>
            <Field label={tr('opd', 'reason')} required>
              <Input value={overrideReason} maxLength={300} onChange={(e) => setOverrideReason(e.target.value)} />
            </Field>
          </div>
        </Dialog>
      )}

      {takingVitals && (
        <VitalsDialog
          open
          onClose={() => setTakingVitals(null)}
          patientId={takingVitals.patientId}
          patientName={`${takingVitals.patientName} · ${takingVitals.mrn}`}
          appointmentId={takingVitals.appointmentId}
          invalidateKeys={[['queue']]}
          newborn={queueNewbornFacts(takingVitals)}
        />
      )}
    </>
  );
}
